import {LabelRegular, Tooltip, clx} from '@hm/fundamental'
import {dataTestId} from '@hproduce/suite-core'
import React from 'react'

interface LegendProps {
  testId?: string
  className?: string
  children: React.ReactNode
}

/**
 * Lays out the legend items in a horizontal row
 */
export const Legend = React.memo(({children, className, testId = 'legend'}: LegendProps) => {
  return (
    <div
      {...dataTestId(testId)}
      className={clx('flex flex-row items-center justify-start gap-4', className)}
    >
      {children}
    </div>
  )
})
Legend.displayName = 'Legend'

interface LegendItemType {
  label: string
  isVisible?: boolean
  indicator?: {
    style?: React.CSSProperties
    height?: NonNullable<React.CSSProperties['width']>
    variant?: 'square' | 'circle'
  }
  id?: string
  testId?: string
  children?: React.ReactNode
}

/**
 * Legend item, intended to be used inside a Legend wrapper. Has a completely configurable indicator.
 */
export const LegendItem = React.memo(
  ({label, isVisible = true, indicator, testId = 'legend_item', id, children}: LegendItemType) => {
    if (!isVisible) return null
    const variant = indicator?.variant || 'square'
    const indicatorHeight = indicator?.height ?? 10
    const isCircle = variant === 'circle'
    const indicatorStyle: React.CSSProperties = {
      justifySelf: 'flex-end',
      width: indicatorHeight,
      height: indicatorHeight,
      borderRadius: isCircle ? '50%' : undefined,
      ...indicator?.style
    }
    return (
      <Tooltip content={label}>
        <div {...dataTestId(testId)}>
          <div id={id} className="flex items-center gap-2">
            {children || <div style={indicatorStyle} />}
            <LabelRegular>{label}</LabelRegular>
          </div>
        </div>
      </Tooltip>
    )
  }
)

LegendItem.displayName = 'LegendItem'
